import type { Evaluation } from '@/domains/evaluation/evaluation.types';

import { getSiteCompliancePrimaryDivisionLabel } from './siteCompliance.selectors';
import type { SiteComplianceSite } from './siteCompliance.types';

export const getSiteComplianceEvaluationReportFilename = ({
  evaluationId,
  siteId,
}: {
  evaluationId: string;
  siteId: string;
}) => `ORR-Evaluation-Report-${siteId}-${evaluationId}`;

export const buildSiteComplianceEvaluationExportRows = ({
  evaluation,
  site,
}: {
  evaluation: Evaluation;
  site: SiteComplianceSite;
}) =>
  evaluation.checkpoints.flatMap((checkpoint) => {
    const baseRow = {
      accountable: checkpoint.metadata.accountable,
      assessmentGroup: checkpoint.metadata.group,
      businessUnits: site.businessUnits.join(', '),
      category: checkpoint.metadata.category,
      checkpointId: checkpoint.metadata.id,
      checkpointName: checkpoint.metadata.displayName,
      checkpointOwner: checkpoint.metadata.owner,
      checkpointReferenceSystem: checkpoint.metadata.referenceSystem,
      checkpointStatus: checkpoint.status,
      checkpointVersion:
        checkpoint.metadata.checkpointVersion ??
        checkpoint.metadata.version ??
        '',
      evaluationId: evaluation.id,
      evaluationScore: evaluation.score,
      generatedAt: evaluation.generatedAt,
      hasException: checkpoint.hasException ? 'Yes' : 'No',
      isScored: checkpoint.metadata.isScored ? 'Yes' : 'No',
      primaryDivision: getSiteCompliancePrimaryDivisionLabel(site),
      primaryDivisions: site.primaryDivisions.join(', '),
      siteId: site.siteId,
      siteName: site.siteName,
      totalAssessments: checkpoint.metadata.totalAssessments,
      totalControls: checkpoint.metadata.totalControls,
    };

    // Checkpoints without per-item assessment rows (e.g. preview/maintenance
    // checkpoints that have not been evaluated yet) still need to appear in
    // the report, so emit a single row with empty item fields.
    if (!checkpoint.items.length) {
      return [
        {
          ...baseRow,
          itemId: '',
          itemType: '',
          reference: '',
          remarks: '',
          status: '',
          system: '',
        },
      ];
    }

    return checkpoint.items.map((item) => ({
      ...baseRow,
      itemId: item.itemId,
      itemType: item.itemType,
      reference: item.reference,
      remarks: item.remarks,
      status: item.status,
      system: item.system,
    }));
  });
